import { Box } from '@material-ui/core';
import { Row } from 'components/layout/Row';
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Column } from 'components/layout/Column';
import format from 'date-fns/format';
import { parseISO } from 'date-fns';
import { DEFAULT_GROUND_COLOR, PLAYER_MARKER_COLOR } from 'components/track/ground/constants';
import { CLUB, ClubTypeRes, ClubTypeVars } from 'query/club';
import { getMarkersColor, getPairedGroundColor, GroundType } from 'models/ground';
import { FPS, loadingTimeFrame, maxSamplesPerFrame } from 'components/constants';
import { useLazyQueryCached, useQueryCached } from '../utils/graphql';
import { TeamSessionsType } from '../../models/team_session';
import {
  SESSION, TEAMSESSION_ATHLETES_SESSIONS, TeamSessionsVars, TeamSessionType,
} from '../../query/session';
import { CACHE_AND_NETWORK } from '../../lib/cache';
import { CursorContext } from '../track/TrackChartContainer';
import ErrorGuard from '../layout/ErrorGuard';
import Ground from './Ground';
import { webplayerWorkerNew, videoWorker } from '../../workers';
import { checkNearEndOfInterval, FpsCtrl, FpsRecordCtrl } from './utils';
import {
  PlayerDataInterval,
  PlayerDataIntervals,
  PlayersDetails,
  PlayersTeams,
  PlayersTeamsContextValue,
  SeriesState,
  WebPlayerRecorderProps,
  WebPlayerWorkerResponse,
} from '../../types/webPlayer';
import { OptionType } from '../Autocomplete';
import { computeAngle, flipPoint, rotate } from '../track/ground/utils';
import { LPSFromGPSPath } from '../../lib/geoNew';
import { withMe } from '../MeUserWrapper';
import { MeType } from '../../models/me';
import { usePlayerStageContext } from './VideoPlayerContext';
import { getWholeSessionTimeSeconds } from '../utils/session';

// @todo verifica se il timestamp del cursore va oltre il limite della traccia
// @todo dopo il nuovo loading il moltiplicatore sembra tornare a 1

const timeFormat = 'H:mm:ss';

export const PlayersTeamsContext = React.createContext<PlayersTeamsContextValue>({
  playersTeams: { asd: ['asdasd'] },
  setPlayersTeams: () => { console.log('default'); },
});

// @todo aggiungere lo stop a fine riproduzione

const WebPlayerRecorder = (props: WebPlayerRecorderProps & { me: MeType }) => {
  const {
    currentDrill,
    defaultGround,
    me,
    isFullscreen,
    sessionGround,
    sessionId,
    showVertices,
    templateId,
    teamId,
  } = props;

  const playerSpeed = 1;

  const [currentActivePlayer, setCurrentActivePlayer] = useState<null | string>(null);
  const [currentHoverPlayer, setCurrentHoverPlayer] = useState<null | string>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [enabledPlayers, setEnabledPlayers] = useState<Array<string>>([]);
  const [ground, setGround] = useState<GroundType | undefined>(sessionGround);
  const [intervals, setIntervals] = useState<PlayerDataIntervals>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [loadingNextData, setLoadingNextData] = useState<string[]>([]);
  const [loadingPlayers, setLoadingPlayers] = useState<Array<string>>([]);
  const [series, setSeries] = useState<SeriesState>({});
  const [tickThrottle, setTickThrottle] = useState(false);
  const [timestamps, setTimestamps] = useState<number[]>([]);
  const [timestampsToAdd, setTimestampsToAdd] = useState<number[]>([]);
  const [workerIsReady, setWorkerIsReady] = useState(false);
  const [currentPlayerStatus, setCurrentPlayerStatus] = useState(false);

  const [playersTeams, setPlayersTeams] = useState<PlayersTeams>({});

  const workerCheckerRef = useRef<any>(null);
  const playerRef = React.useRef<null | HTMLDivElement>(null);
  const currStepRef = useRef(1);
  const currPlayerSpeedRef = useRef(1);
  const fpsCtrlRef = useRef<FpsCtrl | undefined>();
  const fpsRecordCtrlRef = useRef<FpsRecordCtrl | undefined>();
  const lastRequestedTimestamp = useRef<number | null>(null);
  const tickIntervalRef = useRef<ReturnType<typeof setInterval>>();
  const isRecordingFrames = useRef(false);

  const prevPlayerIsLoading = useRef<boolean>(false);
  const playerStageRef = usePlayerStageContext();

  useEffect(() => {
    if (timestamps.length > 0) {
      const wasPlaying = currentPlayerStatus;

      if (wasPlaying) {
        fpsCtrlRef.current?.pause();
      }

      fpsCtrlRef.current = new FpsCtrl(
        FPS,
        (timeData: { frame: number }) => {
          const { frame } = timeData;

          setCurrentTime((prevState) => (lastRequestedTimestamp.current || prevState) + frame * (1 / FPS) * 1000);
        },
        playerSpeed,
      );

      if (wasPlaying) {
        fpsCtrlRef.current.start();
      }
    }
  }, [timestamps, currentPlayerStatus]);

  const findNearTimestamp = useCallback((ts: number, tss?: number[]) => (tss || timestamps).find((t) => (t - ts) < 500 && (t - ts) >= 0), [timestamps]);

  const handleCurrentTimeChange = useCallback((value: number) => {
    lastRequestedTimestamp.current = value;
    setCurrentTime(value);
  }, []);

  const cursorValue = useMemo(() => ({
    cursor: findNearTimestamp(currentTime),
    setCursor: handleCurrentTimeChange,
  }), [currentTime, findNearTimestamp, handleCurrentTimeChange]);

  const {
    loading: sessionLoading,
    data,
  } = useQueryCached<{ res: TeamSessionType }, TeamSessionsVars>(SESSION, {
    variables: {
      drill: currentDrill,
      id: parseInt(sessionId, 10),
      templateId: parseInt(templateId, 10),
      withAthleteSession: true,
      withGround: true,
    },
    ...CACHE_AND_NETWORK,
    onCompleted: (result) => {
      const tracksGround = result?.res?.athleteSessions && result.res.athleteSessions.length > 0
        && result.res.athleteSessions[0] && result.res.athleteSessions[0].track?.ground;

      if (result?.res?.startTimestamp) {
        const timezoneOffset = -new Date(result.res.startTimestamp).getTimezoneOffset() * 60000;
        const newTimestamp = new Date(result.res.startTimestamp).getTime() + timezoneOffset;
        setCurrentTime(newTimestamp);
        lastRequestedTimestamp.current = newTimestamp;
      }

      if (result?.res?.drills?.relatedDrills) {
        const drillsMarks: {
          value: number,
          label: string,
          index?: number,
          id?: number,
          tags?: string[],
        }[] = [];

        result.res.drills.relatedDrills.forEach((drill) => {
          const timezoneOffset = drill?.start
            ? -new Date(drill.start).getTimezoneOffset() * 60000
            : 0;

          drillsMarks.push({
            value: drill ? (new Date(drill.start)).getTime() + timezoneOffset : 0,
            label: drill ? `${format(parseISO(drill.start), timeFormat)}` : '---',
            index: drill?.index || undefined,
            id: drill?.id || undefined,
            tags: drill?.tags || undefined,
          });

          // @todo fare in modo che il valore sia con indice e non timestamp
          drillsMarks.push({
            value: drill ? (new Date(drill.end)).getTime() + timezoneOffset : 0,
            label: drill ? `${format(parseISO(drill.end), timeFormat)}` : '---',
            index: drill?.index || undefined,
            id: drill?.id || undefined,
            tags: drill?.tags || undefined,
          });
        });
      }

      if (tracksGround) {
        setGround(tracksGround);
      }

      // eslint-disable-next-line no-use-before-define
      loadSessions();
    },
  });

  const [loadSessions, {
    data: detailsData,
  }] = useLazyQueryCached<{ res: TeamSessionsType }, TeamSessionsVars>(TEAMSESSION_ATHLETES_SESSIONS, {
    variables: {
      drill: currentDrill,
      fieldsLimit: 6,
      id: parseInt(sessionId, 10),
      templateId: parseInt(templateId, 10),
    },
    fetchPolicy: 'cache-first',
    onCompleted: (d) => {
      setEnabledPlayers(d.res.athleteSessions.map((a) => a.id));
    },
    onError: (error) => {
      console.error('Error fetching athletes', error);
    },
  });

  const isGPSGround = ground?.groundCoordsType !== 'LOCAL';

  const currentGround = !isGPSGround
    ? ground
    : ground || sessionGround;

  const athSess = detailsData?.res.athleteSessions || [];
  const groundDetails = data?.res?.athleteSessions && data.res.athleteSessions.length > 0
    && data.res.athleteSessions[0] && data.res.athleteSessions[0].track?.ground;

  const jwtToken = (localStorage.getItem('exelio_token')) || undefined;
  const maxSamples = loadingTimeFrame * maxSamplesPerFrame;

  useEffect(() => {
    webplayerWorkerNew.onmessage = (e: MessageEvent<WebPlayerWorkerResponse>) => {
      // il worker risponde, è pronto
      if (e.data?.type === 'ready' && !workerIsReady) {
        console.log('%c - webplayerWorker ✔', 'color: green');
        if (workerCheckerRef.current) {
          clearInterval(workerCheckerRef.current);
        }
        setWorkerIsReady(true);
      }

      if (!e.data?.serie?.id || (!athSess || athSess.length === 0)) {
        return;
      }

      const {
        id,
        data: d,
        intervals: dataIntervals,
      } = e.data.serie;

      let tmpPath: {
        t: number,
        x: number | null,
        x_0?: number | null,
        y: number | null,
        y_0?: number | null,
        s: number | null,
        h: number | null,
        hr?: number | null,
      }[] = [];

      if (d && d.length > 0) {
        if (ground?.groundCoordsType === 'LOCAL') { // è un ground LPS
          d.forEach((p) => {
            const point = {
              x: p[1],
              y: p[2],
              s: p[3],
              h: p[4],
              hr: p[5],
            };

            const newPoint = !p[1] || !p[2]
              ? {
                x: null,
                y: null,
                s: null,
                h: null,
                hr: null,
              }
              : groundDetails
                ? defaultGround
                  ? point
                  : {
                    ...rotate(
                      flipPoint(point, groundDetails),
                      computeAngle(groundDetails),
                    ),
                    ...{
                      s: point.s,
                      h: point.h,
                      hr: point.hr,
                    },
                  }
                : point;

            tmpPath.push({
              t: Math.trunc(p[0] * 1000),
              x: newPoint.x,
              y: newPoint.y,
              s: newPoint.s,
              h: newPoint.h,
              hr: newPoint.hr,
            });
          });
        } else {
          try {
            const {
              vertexALatitude,
              vertexALongitude,
              vertexBLatitude,
              vertexBLongitude,
              vertexCLatitude,
              vertexCLongitude,
            } = currentGround || {};

            tmpPath = LPSFromGPSPath(
              d.map((p) => ({
                t: Math.trunc(p[0] * 1000),
                x: p[1],
                y: p[2],
                s: p[3],
                h: p[4],
                hr: p[5],
              })),
              { latitude: vertexALatitude || 0, longitude: vertexALongitude || 0 },
              { latitude: vertexBLatitude || 0, longitude: vertexBLongitude || 0 },
              { latitude: vertexCLatitude || 0, longitude: vertexCLongitude || 0 },
            );
          } catch (err) {
            console.log('Cannot compute LPS from GPS path', err, currentGround);
          }
        }
      } else {
        console.log('No path data', d);
      }

      const pathDataObj = tmpPath.reduce((acc, curr) => {
        acc[curr.t] = {
          x: curr.x || null,
          x_0: curr.x_0 || null,
          y: curr.y || null,
          y_0: curr.y_0 || null,
          s: curr.s || null,
          h: curr.h || null,
          hr: curr.hr || null,
        };
        return acc;
      }, {});

      if ((Object.keys(pathDataObj).length > 0)) {
        setTimestampsToAdd(Object.keys(pathDataObj).map((t) => parseInt(t, 10)).sort());
      }

      setSeries((prevState) => ({
        ...prevState,
        [String(id)]: {
          pathData: pathDataObj,
        },
      }));

      setLoadingPlayers((prevState) => {
        const newLoadingPlayers = [...prevState];
        newLoadingPlayers.splice(newLoadingPlayers.indexOf(String(id)), 1);

        return newLoadingPlayers;
      });

      setLoadingNextData((prevState) => {
        const newLoadingPlayers = [...prevState];
        newLoadingPlayers.splice(newLoadingPlayers.indexOf(String(id)), 1);

        return newLoadingPlayers;
      });

      setIntervals(dataIntervals);
    };
  }, [ground, groundDetails, athSess]);

  const endSessionTimestamp = useMemo(() => {
    if (data?.res?.endTimestamp) {
      return new Date(data.res.endTimestamp).getTime() - new Date(data.res.endTimestamp).getTimezoneOffset() * 60000;
    }

    return 0;
  }, [data?.res?.endTimestamp]);

  const loadAthSessionData = useCallback(async () => {
    if (!detailsData?.res?.athleteSessions || !currentGround) {
      return;
    }

    if (!sessionLoading && athSess.length > 0) {
      const newLoadingPlayers = athSess.map((athS) => athS.id);
      setLoadingPlayers(newLoadingPlayers);

      const timezoneOffset = data?.res.startTimestamp
        ? -new Date(data?.res.startTimestamp).getTimezoneOffset() * 60000
        : 0;
      const startTime = currentTime || (data?.res.startTimestamp
        ? (new Date(data?.res.startTimestamp)).getTime() + timezoneOffset
        : 0);
      const endTime = data?.res.endTimestamp
        ? (new Date(data?.res.endTimestamp)).getTime() + timezoneOffset
        : 0;

      if (jwtToken) {
        webplayerWorkerNew.postMessage({
          action: 'loadMultiplePathData',
          athleteSessionIds: athSess.map((a) => a.id),
          drill: currentDrill,
          jwtToken,
          maxSamples: maxSamplesPerFrame * Math.ceil((endTime - startTime) / 1000),
          start: startTime,
          end: endTime, // 5 minuti
          computeData: {
            currentGround,
            ground,
            groundDetails,
            defaultGround,
          },
        });
      }
    }
  }, [detailsData?.res.athleteSessions, defaultGround, currentDrill, data?.res.endTimestamp, data?.res.startTimestamp, sessionLoading, currentTime, ground]);

  // @todo verifica caching posizioni durante il loading progressivo

  useEffect(() => {
    // attendo che il worker sia pronto
    if (!workerIsReady && !workerCheckerRef.current) {
      workerCheckerRef.current = setInterval(() => {
        webplayerWorkerNew.postMessage({
          action: 'requireInit',
        });
      }, 500);
    }

    return () => {
      if (workerCheckerRef.current) {
        clearInterval(workerCheckerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (timestamps && timestamps.length > 0 && data?.res?.startTimestamp && data?.res?.endTimestamp) {
      currStepRef.current = 1 / FPS;
    }
  }, [timestamps, data?.res?.startTimestamp, data?.res?.endTimestamp]);

  useEffect(() => {
    if (workerIsReady) {
      loadAthSessionData();
    }
  }, [workerIsReady, detailsData, ground, currentDrill]);

  const {
    data: dataGrounds,
  } = useQueryCached<ClubTypeRes, ClubTypeVars>(CLUB, {
    variables: {
      id: teamId,
    },
    ...CACHE_AND_NETWORK,
  });

  const groundSet = dataGrounds?.res?.club?.groundSet || [];

  const groundOptions = useMemo<OptionType[]>(() => groundSet?.map((g) => ({
    id: String(g.id),
    value: String(g.name),
    label: String(g.name),
  })), [groundSet]);

  useEffect(() => {
    if (
      !ground
      && groundOptions.length > 0
      && groundSet.length > 0
      && detailsData
      && data
    ) {
      const selectedGround = groundSet.find((g) => g.id === groundOptions[0].id);

      if (selectedGround) {
        setGround(selectedGround);
      }
    }
  }, [groundOptions, ground, groundSet, detailsData, data]);

  const handlePlayClick = useCallback((forcePlay = false, dontSetLastRequestedTimestamp = false) => {
    if (!dontSetLastRequestedTimestamp) {
      lastRequestedTimestamp.current = currentTime;
    }
    if (
      forcePlay || !currentPlayerStatus
    ) {
      fpsCtrlRef.current?.start();
      setCurrentPlayerStatus(true);
    } else {
      fpsCtrlRef.current?.pause();
      setCurrentPlayerStatus(false);
    }
  }, [currentPlayerStatus, currentTime]);

  // @todo mettere su funzioni a parte, deve pulire eventuali selezioni non attive
  const [playerLinks, setPlayerLinks] = useState<Set<[string, string]>>(new Set());

  const handlePlayerClick = useCallback((playerID: string) => {
    if (!currentActivePlayer) {
      setCurrentActivePlayer(playerID);
    } else if (currentActivePlayer && currentActivePlayer === playerID) {
      setCurrentActivePlayer(null);
    } else {
      const existingLink = Array.from(playerLinks)
        .find((link) => link[0] === currentActivePlayer && link[1] === playerID
        || link[0] === playerID && link[1] === currentActivePlayer);

      if (!existingLink) {
        const newPlayerLinks = new Set(playerLinks);
        newPlayerLinks.add([currentActivePlayer, playerID]);
        setPlayerLinks(newPlayerLinks);
      }

      setCurrentActivePlayer(null);
    }
  }, [currentActivePlayer, playerLinks]);

  const handlePlayerEnter = (playerID: string) => {
    setCurrentHoverPlayer(playerID);
  };

  const handlePlayerLeave = () => {
    setCurrentHoverPlayer(null);
  };

  useEffect(() => {
    currPlayerSpeedRef.current = playerSpeed;
    if (fpsCtrlRef.current) {
      const wasPlaing = currentPlayerStatus;
      if (wasPlaing) {
        handlePlayClick();
      }

      fpsCtrlRef.current.setMultiplier(playerSpeed);

      if (wasPlaing) {
        handlePlayClick(true);
      }
    }
  }, [playerSpeed]);

  // @todo migliorare la verifica, ipoteticamente i timestampsToAdd devono essere solo quelli della prima serie
  useEffect(() => {
    if (timestampsToAdd.length > 0) {
      const wasPlaying = currentPlayerStatus;

      if (wasPlaying) {
        handlePlayClick(false, false);
      }

      setTimestamps((prevValue) => (Array.from(new Set([
        ...timestampsToAdd.sort(),
        ...prevValue,
      ]))));

      setTimestampsToAdd([]);

      if (lastRequestedTimestamp.current) {
        setCurrentTime(lastRequestedTimestamp.current);
      }

      if (wasPlaying) {
        handlePlayClick(true, false);
      }
    }
  }, [timestampsToAdd, currentPlayerStatus]);

  const playersDetails: PlayersDetails = useMemo(() => (detailsData?.res?.athleteSessions
    ? detailsData?.res?.athleteSessions.reduce((acc, curr) => {
      if (curr.athlete?.id) {
        acc[curr.id] = {
          id: curr.athlete?.id,
          name: curr.athlete?.name,
          number: curr.athlete?.playerSet.find((ps) => ps.team?.id && ps.team?.id === teamId)?.number || null,
          shortName: curr.athlete?.shortName,
        };
      }

      return acc;
    }, {})
    : {}), [detailsData?.res?.athleteSessions, teamId]);

  const handleRemoveLink = (linkIdx: number) => {
    // eslint-disable-next-line no-restricted-globals
    const newPlayerLinks = Array.from(playerLinks);
    newPlayerLinks.splice(linkIdx, 1);
    setPlayerLinks(new Set(newPlayerLinks));
  };

  const defaultPlayersColor = !isGPSGround
    ? ground?.groundSurfaceColor ? getPairedGroundColor(ground?.groundSurfaceColor) : PLAYER_MARKER_COLOR
    : sessionGround?.groundSurfaceColor
      ? getPairedGroundColor(sessionGround?.groundSurfaceColor)
      : PLAYER_MARKER_COLOR;

  useEffect(() => {
    // check if currentTime is near the end of an interval
    checkNearEndOfInterval(
      currentTime,
      intervals,
      (interval: PlayerDataInterval) => {
        if (loadingNextData.length === 0) {
          const athleteSessionIds = athSess.map((a) => a.id);

          setLoadingNextData(athleteSessionIds);
          setLoadingPlayers(athleteSessionIds);

          if (jwtToken) {
            webplayerWorkerNew.postMessage({
              action: 'loadMultiplePathData',
              athleteSessionIds,
              drill: currentDrill,
              jwtToken,
              maxSamples,
              start: interval[1],
              end: interval[1] + loadingTimeFrame * 1000, // 5 minuti
              computeData: {
                currentGround,
                ground,
                groundDetails,
                defaultGround,
              },
            });
          }
        }
      },
      20000 * playerSpeed,
    );

    if (currentTime && endSessionTimestamp && currentTime >= endSessionTimestamp) {
      setIsRecording(false);
      isRecordingFrames.current = false;
      fpsRecordCtrlRef.current?.pause();
      console.log('stop recording');
      videoWorker.postMessage({
        action: 'getVideo',
      });

      setTimeout(() => {
        console.log('close window');
        window.dispatchEvent(new CustomEvent('clientEvent', {
          detail: {
            action: 'closeTheRecorder',
          },
        }));
      }, 1000);
    }
  }, [currentTime, intervals, endSessionTimestamp]);

  useEffect(() => {
    tickIntervalRef.current = setInterval(() => {
      setTickThrottle(!tickThrottle);
    }, 300);

    return () => {
      if (tickIntervalRef.current) {
        clearInterval(tickIntervalRef.current);
      }
    };
  }, []);

  const isLoadingData = !currentPlayerStatus && loadingPlayers.length > 0;
  const userUnits = useMemo(() => me.userunits, [me.userunits]);

  const handleRecordTrack = async () => {
    if (isRecording) {
      // @todo stop recording and show download video
      setIsRecording(false);
      isRecordingFrames.current = false;
      fpsRecordCtrlRef.current?.pause();
      console.log('stop recording');

      // @todo-restore debug
      // await videoRecorder.current?.stopRecording();

      videoWorker.postMessage({
        action: 'getVideo',
      });
    } else {
      const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

      if (data?.res?.startTimestamp) {
        setIsRecording(true);
        isRecordingFrames.current = true;

        const timezoneOffset = -new Date(data.res.startTimestamp).getTimezoneOffset() * 60000;
        // @todo lastRequestedTimestamp.current ?
        const newTimestamp = new Date(data.res.startTimestamp).getTime() + timezoneOffset;
        setCurrentTime(newTimestamp);
        lastRequestedTimestamp.current = newTimestamp;

        fpsRecordCtrlRef.current = new FpsRecordCtrl(
          1e3 / FPS,
          newTimestamp,
          endSessionTimestamp,
          async (values: {time: number, frame: number}) => {
            const { time, frame } = values;
            if (playerStageRef?.current && isRecordingFrames.current) {
              videoWorker.postMessage({
                action: 'recordFrame',
                frameData: playerStageRef.current.toDataURL(),
                frameTime: frame * 1e6 / FPS,
              });
            }

            await delay(0);

            setCurrentTime(() => time);
          },
        );

        videoWorker.postMessage({
          action: 'setupVideo',
          fps: FPS,
        });

        videoWorker.onmessage = (e) => {
          const { type, video } = e.data;
          if (type === 'video') {
            const videoBlob = new Blob([video], { type: 'video/mp4' });
            const videoUrl = URL.createObjectURL(videoBlob);
            const a = document.createElement('a');
            a.href = videoUrl;
            a.download = 'video.mp4';
            a.click();
            a.remove();
          }
        };
        fpsRecordCtrlRef.current?.start();
      }
    }
  };

  useEffect(() => {
    if (!isLoadingData && prevPlayerIsLoading.current && !isRecording) {
      if (window.playerIsReady) {
        window.playerStartRecording = () => {
          console.log('[event] playerStartRecording');
          handleRecordTrack();
        };

        window.playerIsReady({
          duration: (data
            ? currentDrill !== null && data
              ? data.res.drills.relatedDrills[currentDrill].totalTime.value
              : getWholeSessionTimeSeconds(data.res)
            : 0) * 1000,
        });
      }
    }

    prevPlayerIsLoading.current = isLoadingData;
  }, [isLoadingData]);

  const teamColors = useMemo(() => getMarkersColor(currentGround?.groundSurfaceColor || DEFAULT_GROUND_COLOR), []);

  const playersTeamsContextValue = useMemo(() => ({ playersTeams, setPlayersTeams }), [playersTeams, setPlayersTeams]);

  return (
    <ErrorGuard>
      <div className="tracks-player" ref={playerRef}>
        <Row className="tracks-player-container">
          <PlayersTeamsContext.Provider value={playersTeamsContextValue}>
            <Column xs={isFullscreen ? 12 : 8} className="tracks-player-column">
              <Box p={isFullscreen ? 0 : 2} style={{ paddingLeft: isFullscreen ? '0' : '10px' }}>
                <CursorContext.Provider value={cursorValue}>
                  {
                    currentGround
                    && (
                      <Ground
                        currentActivePlayer={currentActivePlayer}
                        hoverPlayer={currentHoverPlayer}
                        defaultPlayersColor={defaultPlayersColor}
                        enabledPlayers={enabledPlayers}
                        ground={currentGround}
                        handlePlayerClick={handlePlayerClick}
                        handlePlayerEnter={handlePlayerEnter}
                        handlePlayerLeave={handlePlayerLeave}
                        handleRemoveLink={handleRemoveLink}
                        infoPlayers={[]}
                        isLoadingData={isLoadingData}
                        jerseyOrNumber={false}
                        playerLinks={playerLinks}
                        playersColors={{}}
                        playersDetails={playersDetails}
                        series={series}
                        showVertices={showVertices}
                        teamColors={teamColors}
                        trailsEnabled={false}
                        userUnits={userUnits}
                      />
                    )
                  }
                </CursorContext.Provider>
              </Box>
            </Column>

          </PlayersTeamsContext.Provider>
        </Row>

      </div>
    </ErrorGuard>
  );
};

export default withMe(WebPlayerRecorder);
